import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, Bell, BellOff, Lock, User, 
  Camera, Moon, Sun, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { getInitials } from '@/lib/utils';
import { authAPI, uploadAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';

export const SettingsPage = ({ user, onClose, onUserUpdate, isDark, setIsDark }) => {
  const [username, setUsername] = useState(user?.username || '');
  const [pushEnabled, setPushEnabled] = useState(user?.push_enabled !== false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleUpdateProfile = async () => {
    if (!username.trim()) {
      toast.error('Benutzername darf nicht leer sein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await authAPI.updateMe({ username: username.trim() });
      onUserUpdate(response.data);
      toast.success('Profil aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Neues Passwort muss mindestens 6 Zeichen haben');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Passwort geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePush = async () => {
    const newValue = !pushEnabled;
    setPushEnabled(newValue);
    
    try {
      await api.put(`/push/toggle?enabled=${newValue}`);
      onUserUpdate({ ...user, push_enabled: newValue });
      toast.success(newValue ? 'Benachrichtigungen aktiviert' : 'Benachrichtigungen deaktiviert');
    } catch (error) {
      setPushEnabled(!newValue);
      toast.error('Fehler beim Ändern der Einstellung');
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Bitte nur Bilder auswählen');
      return;
    }

    setUploadingAvatar(true);
    try {
      const uploadRes = await uploadAPI.upload(file);
      const avatarUrl = uploadRes.data.url;
      
      const response = await authAPI.updateMe({ avatar: avatarUrl });
      onUserUpdate(response.data);
      toast.success('Profilbild aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-xl">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Einstellungen
          </h1>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6 max-w-md mx-auto">
          {/* Profile Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm"
          >
            <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Profil
            </h2>

            {/* Avatar */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="text-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                    {getInitials(user?.username)}
                  </AvatarFallback>
                </Avatar>
                <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center cursor-pointer shadow-lg">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  {uploadingAvatar ? (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Camera className="w-4 h-4 text-white" />
                  )}
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Benutzername</Label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="rounded-xl bg-slate-100 dark:bg-slate-800"
                />
              </div>

              <Button
                onClick={handleUpdateProfile}
                disabled={loading}
                className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-600"
              >
                {loading ? 'Speichern...' : 'Profil speichern'}
              </Button>
            </div>
          </motion.div>

          {/* Password Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm"
          >
            <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Passwort ändern
            </h2>

            {/* WICHTIG: div zu form geändert & onSubmit hinzugefügt */}
            <form 
              className="space-y-4" 
              onSubmit={(e) => {
                e.preventDefault();
                handleChangePassword();
              }}
           >
              {/* --- NEU: Verstecktes Feld für den Browser/Passwort-Manager --- */}
              <input 
                type="text" 
                name="username" 
                value={user?.username || ''} 
                autoComplete="username" 
                className="hidden" 
                readOnly 
              />

            >
              <div className="space-y-2">
                <Label>Aktuelles Passwort</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="rounded-xl"
                  autoComplete="current-password" 
                />
              </div>

              <div className="space-y-2">
                <Label>Neues Passwort</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  className="rounded-xl"
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-2">
                <Label>Passwort bestätigen</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="rounded-xl"
                  autoComplete="new-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                variant="outline"
                className="w-full rounded-xl"
              >
                {loading ? 'Ändern...' : 'Passwort ändern'}
              </Button>
            </form>
          </motion.div>

          {/* Notifications Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm"
          >
            <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Benachrichtigungen
            </h2>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {pushEnabled ? (
                  <Bell className="w-5 h-5 text-emerald-500" />
                ) : (
                  <BellOff className="w-5 h-5 text-slate-400" />
                )}
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    Push-Benachrichtigungen
                  </p>
                  <p className="text-sm text-slate-500">
                    Ton bei neuen Nachrichten
                  </p>
                </div>
              </div>
              <Switch
                checked={pushEnabled}
                onCheckedChange={handleTogglePush}
              />
            </div>
          </motion.div>

          {/* Appearance Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm"
          >
            <h2 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              Darstellung
            </h2>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isDark ? (
                  <Moon className="w-5 h-5 text-purple-500" />
                ) : (
                  <Sun className="w-5 h-5 text-amber-500" />
                )}
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    Dunkler Modus
                  </p>
                  <p className="text-sm text-slate-500">
                    {isDark ? 'Aktiviert' : 'Deaktiviert'}
                  </p>
                </div>
              </div>
              <Switch
                checked={isDark}
                onCheckedChange={setIsDark}
              />
            </div>
          </motion.div>

          {/* Admin Badge */}
          {user?.is_superadmin && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-purple-50 dark:bg-purple-900/20 rounded-2xl p-4 border border-purple-200 dark:border-purple-800"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                <div>
                  <p className="font-medium text-purple-900 dark:text-purple-100">
                    Superadmin
                  </p>
                  <p className="text-sm text-purple-600 dark:text-purple-300">
                    Sie haben administrative Rechte
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default SettingsPage;
